/**
 * Importador cliente-side (sin IA / sin créditos) de cuadros de leasing y préstamos.
 * Lee Excel/CSV con ExcelJS, permite mapear columnas y da de alta contrato + cuadro.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import ExcelJS from "exceljs";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Upload, FileSpreadsheet, AlertCircle, CheckCircle2, Eye, XCircle } from "lucide-react";
import { normalizeSupplierNif } from "@/lib/leasingLookup";
import { formatNumber } from "@/lib/numberFormatting";

type Kind = "leasing" | "loan";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  kind: Kind;
  onImported?: () => void;
}

interface Row { [k: string]: any }
interface CompanyOpt { id: string; name: string }
interface ContractOpt { id: string; label: string }

type ScheduleField = "installment_number" | "due_date" | "principal" | "interest" | "insurance" | "total" | "pending_capital";

const SCHEDULE_FIELDS: { key: ScheduleField; label: string; required?: boolean }[] = [
  { key: "installment_number", label: "Nº cuota" },
  { key: "due_date", label: "Fecha vencimiento", required: true },
  { key: "principal", label: "Amortización / Capital", required: true },
  { key: "interest", label: "Intereses", required: true },
  { key: "insurance", label: "Seguro" },
  { key: "total", label: "Total cuota" },
  { key: "pending_capital", label: "Capital pendiente" },
];

// Heurísticas de auto-detección por nombre de cabecera
const AUTO_MAP: Record<ScheduleField, RegExp[]> = {
  installment_number: [/n[ºo°]?\s*cuota/i, /cuota/i, /recibo/i, /n[ºo°]\s*recibo/i, /^n[ºo°]$/i],
  due_date: [/fecha.*(venc|cargo|cobro|vto)/i, /vencimiento/i, /fecha\s*vto/i, /^fecha$/i, /vto/i],
  principal: [/amortiz/i, /capital(?!\s*pend)/i, /principal/i, /recup/i, /coste/i, /capital\s*amortizado/i],
  interest: [/inter[eé]s/i, /financ/i, /^int\b/i, /intereses/i],
  insurance: [/seguro/i, /prima/i],
  total: [/total/i, /importe.*cuota/i, /^cuota$/i, /^importe$/i, /total\s*recibo/i],
  pending_capital: [/pend/i, /saldo/i, /resto/i, /deuda/i, /capital\s*pendiente/i, /principal\s*pendiente/i, /saldo\s*vivo/i, /capital\s*vivo/i, /deuda\s*pendiente/i],
};

// Convertir índice de columna (0-based) a letra de Excel (A, B... Z, AA, AB...)
const indexToColumnLetter = (idx: number): string => {
  let result = "";
  let n = idx + 1;
  while (n > 0) {
    const rem = (n - 1) % 26;
    result = String.fromCharCode(65 + rem) + result;
    n = Math.floor((n - 1) / 26);
  }
  return result || "A";
};

const parseNumber = (v: any): number => {
  if (v == null || v === "") return 0;
  if (typeof v === "number") return v;
  const s = String(v).trim().replace(/[€\s]/g, "");
  // ES format: 1.234,56 ; also handle 1,234.56
  const hasComma = s.includes(",");
  const hasDot = s.includes(".");
  let n: number;
  if (hasComma && hasDot) {
    n = Number(s.replace(/\./g, "").replace(",", "."));
  } else if (hasComma) {
    n = Number(s.replace(",", "."));
  } else {
    n = Number(s);
  }
  return Number.isFinite(n) ? n : 0;
};

const parseDate = (v: any): string | null => {
  if (!v) return null;
  if (v instanceof Date) return v.toISOString().slice(0, 10);
  const s = String(v).trim();
  // dd/mm/yyyy o dd-mm-yyyy
  const m = s.match(/^(\d{1,2})[\/\-.](\d{1,2})[\/\-.](\d{2,4})$/);
  if (m) {
    let [_, d, mo, y] = m;
    if (y.length === 2) y = (Number(y) > 50 ? "19" : "20") + y;
    return `${y.padStart(4, "0")}-${mo.padStart(2, "0")}-${d.padStart(2, "0")}`;
  }
  // yyyy-mm-dd
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  const d2 = new Date(s);
  return isNaN(d2.getTime()) ? null : d2.toISOString().slice(0, 10);
};

const formatPreviewValue = (v: any): string => {
  if (v == null || v === "") return "";
  if (v instanceof Date) return v.toLocaleDateString("es-ES");
  return String(v).slice(0, 25);
};

const ImportScheduleDialog = ({ open, onOpenChange, kind, onImported }: Props) => {
  const { toast } = useToast();
  const isLoan = kind === "loan";
  const fileRef = useRef<HTMLInputElement>(null);

  const [companies, setCompanies] = useState<CompanyOpt[]>([]);
  const [contracts, setContracts] = useState<ContractOpt[]>([]);
  const [companyId, setCompanyId] = useState<string>("");
  const [mode, setMode] = useState<"new" | "existing">("new");
  const [existingContractId, setExistingContractId] = useState<string>("");

  // Contract fields (new)
  const [contractNumber, setContractNumber] = useState("");
  const [description, setDescription] = useState("");
  const [plateNumber, setPlateNumber] = useState(""); // leasing
  const [supplierTaxId, setSupplierTaxId] = useState(""); // leasing
  const [bankName, setBankName] = useState(""); // loan
  const [vatHalf, setVatHalf] = useState(false); // leasing
  const [costRecoveryAccount, setCostRecoveryAccount] = useState(""); // leasing
  const [financialChargeAccount, setFinancialChargeAccount] = useState(""); // leasing
  const [insuranceAccount, setInsuranceAccount] = useState(""); // leasing
  const [suppliers, setSuppliers] = useState<{ tax_id: string; description: string }[]>([]);
  const [accountCatalog, setAccountCatalog] = useState<{ account: string; description: string }[]>([]);

  // File / sheet / mapping
  const [fileName, setFileName] = useState("");
  const [sheetNames, setSheetNames] = useState<string[]>([]);
  const [sheetIndex, setSheetIndex] = useState<number>(0);
  const [rawGrid, setRawGrid] = useState<any[][]>([]); // matrix per selected sheet
  const [allSheets, setAllSheets] = useState<any[][][]>([]); // matrix per sheet
  const [headerRow, setHeaderRow] = useState<number>(1); // 1-based
  const [mapping, setMapping] = useState<Record<ScheduleField, number | null>>({
    installment_number: null, due_date: null, principal: null,
    interest: null, insurance: null, total: null, pending_capital: null,
  });
  const [importing, setImporting] = useState(false);
  const [showRawPreview, setShowRawPreview] = useState(false);

  // Load companies
  useEffect(() => {
    if (!open) return;
    (async () => {
      const { data } = await supabase.from("company_masters").select("id, name").order("name");
      setCompanies((data || []) as any);
    })();
  }, [open]);

  // Load contracts for selected company
  useEffect(() => {
    if (!companyId) { setContracts([]); return; }
    (async () => {
      const table = isLoan ? "loan_contracts" : "leasing_contracts";
      const { data } = await supabase
        .from(table).select("id, contract_number, description")
        .eq("company_id", companyId).order("contract_number");
      setContracts(((data as any[]) || []).map((c) => ({
        id: c.id, label: `${c.contract_number}${c.description ? ` — ${c.description}` : ""}`,
      })));
    })();
  }, [companyId, isLoan]);

  // Load suppliers + account catalog for the selected company (leasing)
  useEffect(() => {
    if (!companyId || isLoan) { setSuppliers([]); setAccountCatalog([]); return; }
    let mounted = true;
    (async () => {
      const [supRes, catRes] = await Promise.all([
        supabase.from("supplier_master").select("tax_id, description").eq("company_id", companyId).order("description"),
        supabase.from("company_account_catalog").select("account, description").eq("company_id", companyId).order("account"),
      ]);
      if (!mounted) return;
      const seen = new Set<string>();
      const list: { tax_id: string; description: string }[] = [];
      ((supRes.data as any[]) || []).forEach((s) => {
        const nif = normalizeSupplierNif(s.tax_id || "");
        if (!nif || seen.has(nif)) return;
        seen.add(nif);
        list.push({ tax_id: nif, description: s.description || nif });
      });
      setSuppliers(list);
      setAccountCatalog(((catRes.data as any[]) || []) as { account: string; description: string }[]);
    })();
    return () => { mounted = false; };
  }, [companyId, isLoan]);

  const resetFile = () => {
    setFileName(""); setSheetNames([]); setAllSheets([]); setRawGrid([]);
    setHeaderRow(1); setMapping({
      installment_number: null, due_date: null, principal: null,
      interest: null, insurance: null, total: null, pending_capital: null,
    });
    setShowRawPreview(false);
  };

  const normalizeCellValue = (v: any): any => {
    if (v == null) return "";
    if (v instanceof Date) return v;
    if (typeof v === "object") {
      if ("result" in v) return v.result;
      if ("text" in v) return v.text;
      if ("richText" in v) return v.richText.map((r: any) => r.text).join("");
      return String(v);
    }
    return v;
  };

  const handleFile = async (f: File) => {
    setFileName(f.name);
    try {
      const buf = await f.arrayBuffer();
      if (f.name.toLowerCase().endsWith(".csv")) {
        const text = new TextDecoder().decode(buf);
        const sep = text.split("\n")[0].includes(";") ? ";" : ",";
        const rows = text.split(/\r?\n/).map((l) => l.split(sep));
        const maxCols = Math.max(0, ...rows.map((r) => r.length));
        const padded = rows.map((r) => [...r, ...Array(maxCols - r.length).fill("")]);
        setAllSheets([padded]); setSheetNames(["CSV"]); setSheetIndex(0); setRawGrid(padded);
      } else {
        const wb = new ExcelJS.Workbook();
        await wb.xlsx.load(buf);
        const sheets: any[][][] = [];
        const names: string[] = [];
        wb.eachSheet((ws) => {
          names.push(ws.name);
          const grid: any[][] = [];
          ws.eachRow({ includeEmpty: true }, (row) => {
            const arr: any[] = [];
            row.eachCell({ includeEmpty: true }, (cell) => {
              arr.push(normalizeCellValue((cell as any).value));
            });
            grid.push(arr);
          });
          // Pad all rows to the same width (max columns found in the sheet)
          const maxCols = Math.max(0, ...grid.map((r) => r.length));
          const padded = grid.map((r) => [...r, ...Array(maxCols - r.length).fill("")]);
          sheets.push(padded);
        });
        setAllSheets(sheets); setSheetNames(names); setSheetIndex(0);
        setRawGrid(sheets[0] || []);
      }
    } catch (e: any) {
      toast({ title: "Error leyendo archivo", description: e.message, variant: "destructive" });
      resetFile();
    }
  };

  useEffect(() => {
    if (allSheets.length > 0) setRawGrid(allSheets[sheetIndex] || []);
  }, [sheetIndex, allSheets]);

  // Auto-map when header row changes
  const headers = useMemo<string[]>(() => {
    const row = rawGrid[headerRow - 1] || [];
    return row.map((c) => (c == null ? "" : String(c).trim()));
  }, [rawGrid, headerRow]);

  // Column metadata: letter, header, first non-empty value, is empty
  const columnMeta = useMemo(() => {
    return headers.map((h, i) => {
      let firstValue: any = "";
      for (let r = headerRow; r < rawGrid.length; r++) {
        const v = rawGrid[r]?.[i];
        if (v != null && v !== "") {
          firstValue = v;
          break;
        }
      }
      const isEmpty = (h === "" || h == null) && firstValue === "";
      return {
        index: i,
        letter: indexToColumnLetter(i),
        header: h,
        firstValue: formatPreviewValue(firstValue),
        isEmpty,
      };
    }).filter((c) => !c.isEmpty);
  }, [headers, rawGrid, headerRow]);

  useEffect(() => {
    if (headers.length === 0) return;
    const next: Record<ScheduleField, number | null> = {
      installment_number: null, due_date: null, principal: null,
      interest: null, insurance: null, total: null, pending_capital: null,
    };
    (Object.keys(AUTO_MAP) as ScheduleField[]).forEach((field) => {
      const patterns = AUTO_MAP[field];
      const idx = headers.findIndex((h) => patterns.some((p) => p.test(h)));
      if (idx >= 0) next[field] = idx;
    });
    setMapping(next);
  }, [headers]);

  const preview = useMemo(() => {
    const rows: any[] = [];
    for (let i = headerRow; i < rawGrid.length; i++) {
      const r = rawGrid[i];
      if (!r || r.every((c) => c == null || c === "")) continue;
      const due = mapping.due_date != null ? parseDate(r[mapping.due_date]) : null;
      const principal = mapping.principal != null ? parseNumber(r[mapping.principal]) : 0;
      const interest = mapping.interest != null ? parseNumber(r[mapping.interest]) : 0;
      const insurance = mapping.insurance != null ? parseNumber(r[mapping.insurance]) : 0;
      let total = mapping.total != null ? parseNumber(r[mapping.total]) : 0;
      if (!total) total = principal + interest + insurance;
      const pend = mapping.pending_capital != null ? parseNumber(r[mapping.pending_capital]) : null;
      const num = mapping.installment_number != null ? parseNumber(r[mapping.installment_number]) : rows.length + 1;
      if (!due && !principal && !interest && !total) continue;
      rows.push({
        installment_number: Math.round(num) || rows.length + 1,
        due_date: due, principal, interest, insurance, total,
        pending_capital: pend,
      });
    }
    return rows;
  }, [rawGrid, headerRow, mapping]);

  const totals = useMemo(() => {
    const t = { principal: 0, interest: 0, insurance: 0, total: 0, count: preview.length };
    preview.forEach((r) => { t.principal += r.principal; t.interest += r.interest; t.insurance += r.insurance; t.total += r.total; });
    return t;
  }, [preview]);

  const mappedCount = useMemo(() => Object.values(mapping).filter((v) => v != null).length, [mapping]);
  const missingRequired = useMemo(() => SCHEDULE_FIELDS.filter((f) => f.required && mapping[f.key] == null).map((f) => f.label), [mapping]);

  const canImport = companyId && preview.length > 0 && (
    (mode === "existing" && existingContractId) ||
    (mode === "new" && contractNumber.trim())
  ) && mapping.due_date != null && mapping.principal != null;

  const doImport = async () => {
    setImporting(true);
    try {
      let contractId = existingContractId;
      if (mode === "new") {
        if (isLoan) {
          const { data, error } = await supabase.from("loan_contracts").insert({
            company_id: companyId,
            contract_number: contractNumber.trim(),
            description: description.trim() || null,
            bank_name: bankName.trim() || null,

            installments_count: preview.length,
            start_date: preview[0]?.due_date || null,
            end_date: preview[preview.length - 1]?.due_date || null,
            principal: totals.principal || null,
          }).select("id").single();
          if (error) throw error;
          contractId = data.id;
        } else {
          const { data, error } = await supabase.from("leasing_contracts").insert({
            company_id: companyId,
            contract_number: contractNumber.trim(),
            description: description.trim() || contractNumber.trim(),
            plate_number: plateNumber.trim() || "—",
            supplier_tax_id: normalizeSupplierNif(supplierTaxId),
            vat_deduction_half: vatHalf,
            cost_recovery_account: costRecoveryAccount.trim() || null,
            financial_charge_account: financialChargeAccount.trim() || null,
            insurance_account: insuranceAccount.trim() || null,
          }).select("id").single();
          if (error) throw error;
          contractId = data.id;
        }
      }

      // Insert schedule rows
      const scheduleTable = isLoan ? "loan_schedule" : "leasing_schedule";
      const rows = preview
        .filter((r) => r.due_date)
        .map((r) => isLoan ? {
          contract_id: contractId,
          installment_number: r.installment_number,
          due_date: r.due_date,
          principal_amount: r.principal,
          interest_amount: r.interest,
          insurance: r.insurance,
          total_amount: r.total,
          pending_capital: r.pending_capital,
        } : {
          contract_id: contractId,
          installment_number: r.installment_number,
          due_date: r.due_date,
          cost_recovery: r.principal,
          financial_charge: r.interest,
          insurance: r.insurance,
          total_amount: r.total,
          pending_capital: r.pending_capital,
        });

      // Chunked inserts to avoid payload limits
      const chunk = 500;
      for (let i = 0; i < rows.length; i += chunk) {
        const { error } = await supabase.from(scheduleTable).insert(rows.slice(i, i + chunk));
        if (error) throw error;
      }

      toast({
        title: "Importación completada",
        description: `${rows.length} cuotas añadidas al contrato.`,
      });
      onImported?.();
      onOpenChange(false);
      // Reset form
      resetFile(); setContractNumber(""); setDescription(""); setPlateNumber("");
      setSupplierTaxId(""); setBankName(""); setVatHalf(false);
      setCostRecoveryAccount(""); setFinancialChargeAccount(""); setInsuranceAccount("");
      setExistingContractId(""); setMode("new");
    } catch (e: any) {
      toast({ title: "Error importando", description: e.message, variant: "destructive" });
    } finally {
      setImporting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSpreadsheet className="h-5 w-5" />
            Importar cuadro de {isLoan ? "préstamo" : "leasing"} desde Excel/CSV
          </DialogTitle>
          <DialogDescription>
            Sin consumo de créditos de IA. El archivo se procesa localmente en tu navegador.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Paso 1: Cliente y contrato */}
          <section className="border rounded-lg p-3 space-y-3">
            <h3 className="text-sm font-semibold">1. Cliente y contrato</h3>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Cliente</Label>
                <Select value={companyId} onValueChange={setCompanyId}>
                  <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Selecciona empresa" /></SelectTrigger>
                  <SelectContent>
                    {companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Modo</Label>
                <Select value={mode} onValueChange={(v: any) => setMode(v)}>
                  <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="new">Crear contrato nuevo</SelectItem>
                    <SelectItem value="existing">Añadir a contrato existente</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {mode === "existing" ? (
              <div>
                <Label className="text-xs">Contrato existente</Label>
                <Select value={existingContractId} onValueChange={setExistingContractId} disabled={!companyId}>
                  <SelectTrigger className="h-8 text-xs"><SelectValue placeholder={companyId ? "Selecciona contrato" : "Elige cliente primero"} /></SelectTrigger>
                  <SelectContent>
                    {contracts.map((c) => <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Nº contrato</Label>
                  <Input className="h-8 text-xs" value={contractNumber} onChange={(e) => setContractNumber(e.target.value)} />
                </div>
                <div>
                  <Label className="text-xs">Descripción</Label>
                  <Input className="h-8 text-xs" value={description} onChange={(e) => setDescription(e.target.value)} />
                </div>
                {isLoan ? (
                  <div>
                    <Label className="text-xs">Entidad</Label>
                    <Input className="h-8 text-xs" value={bankName} onChange={(e) => setBankName(e.target.value)} />
                  </div>
                ) : (
                  <>
                    <div>
                      <Label className="text-xs">Matrícula</Label>
                      <Input className="h-8 text-xs" value={plateNumber} onChange={(e) => setPlateNumber(e.target.value)} />
                    </div>
                    <div>
                      <Label className="text-xs">Proveedor (NIF)</Label>
                      <Input
                        className="h-8 text-xs"
                        list="import-supplier-list"
                        value={supplierTaxId}
                        onChange={(e) => setSupplierTaxId(e.target.value)}
                        onBlur={() => setSupplierTaxId((v) => normalizeSupplierNif(v))}
                        placeholder={companyId ? "Elige del maestro o escribe el NIF" : "Elige cliente primero"}
                      />
                      <datalist id="import-supplier-list">
                        {suppliers.map((s) => (
                          <option key={s.tax_id} value={s.tax_id}>{s.description}</option>
                        ))}
                      </datalist>
                      <p className="text-[10px] text-muted-foreground truncate min-h-[14px]">
                        {suppliers.find((s) => s.tax_id === normalizeSupplierNif(supplierTaxId))?.description || ""}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 pt-5">
                      <Checkbox id="vathalf" checked={vatHalf} onCheckedChange={(v) => setVatHalf(!!v)} />
                      <Label htmlFor="vathalf" className="text-xs cursor-pointer">{"\n"}</Label>
                    </div>
                    <div className="col-span-2 grid grid-cols-1 sm:grid-cols-3 gap-3 rounded border bg-muted/30 p-2">
                      <datalist id="import-account-catalog">
                        {accountCatalog.map((a) => (
                          <option key={a.account} value={a.account}>{a.description}</option>
                        ))}
                      </datalist>
                      {([
                        ["Recuperación de coste (524)", costRecoveryAccount, setCostRecoveryAccount, "5240000"],
                        ["Carga financiera (6623)", financialChargeAccount, setFinancialChargeAccount, "66230010"],
                        ["Seguro (625)", insuranceAccount, setInsuranceAccount, "6250000"],
                      ] as [string, string, (v: string) => void, string][]).map(([label, value, setValue, ph]) => (
                        <div key={label}>
                          <Label className="text-xs">{label}</Label>
                          <Input
                            className="h-8 text-xs font-mono"
                            list="import-account-catalog"
                            value={value}
                            onChange={(e) => setValue(e.target.value)}
                            placeholder={ph}
                          />
                          <p className="text-[10px] text-muted-foreground truncate min-h-[14px]">
                            {accountCatalog.find((a) => a.account === value.trim())?.description || ""}
                          </p>
                        </div>
                      ))}
                      <p className="col-span-full text-[10px] text-muted-foreground">
                        Estas cuentas se usarán al contabilizar las facturas que casen con este contrato (por nº de contrato y/o matrícula).
                      </p>
                    </div>

                  </>
                )}
              </div>
            )}
          </section>

          {/* Paso 2: Archivo */}
          <section className="border rounded-lg p-3 space-y-3">
            <h3 className="text-sm font-semibold">2. Archivo</h3>
            <div className="flex items-center gap-3">
              <input
                ref={fileRef} type="file" accept=".xlsx,.xls,.csv" className="hidden"
                onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFile(f); }}
              />
              <Button type="button" variant="outline" size="sm" onClick={() => fileRef.current?.click()}>
                <Upload className="h-4 w-4 mr-2" /> Elegir Excel/CSV
              </Button>
              {fileName && <span className="text-xs text-muted-foreground">{fileName}</span>}
            </div>
            {sheetNames.length > 0 && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Hoja</Label>
                  <Select value={String(sheetIndex)} onValueChange={(v) => setSheetIndex(Number(v))}>
                    <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {sheetNames.map((n, i) => <SelectItem key={i} value={String(i)}>{n}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Fila de cabeceras (1-{rawGrid.length})</Label>
                  <Input type="number" min={1} max={rawGrid.length} className="h-8 text-xs"
                    value={headerRow} onChange={(e) => setHeaderRow(Math.max(1, Number(e.target.value) || 1))} />
                </div>
              </div>
            )}

            {/* Vista previa cruda del archivo */}
            {rawGrid.length > 0 && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <p className="text-xs text-muted-foreground">
                    {columnMeta.length} columnas detectadas · {rawGrid.length} filas leídas
                  </p>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-7 text-xs gap-1"
                    onClick={() => setShowRawPreview((v) => !v)}
                  >
                    <Eye className="h-3.5 w-3.5" />
                    {showRawPreview ? "Ocultar vista previa" : "Ver archivo tal cual se lee"}
                  </Button>
                </div>
                {showRawPreview && (
                  <div className="max-h-60 overflow-auto border rounded">
                    <Table>
                      <TableHeader className="sticky top-0 bg-muted">
                        <TableRow>
                          {columnMeta.map((c) => (
                            <TableHead key={c.index} className="text-xs whitespace-nowrap">
                              {c.letter}: {c.header || "(sin título)"}
                            </TableHead>
                          ))}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {rawGrid.slice(headerRow - 1, headerRow + 9).map((r, i) => (
                          <TableRow key={i}>
                            {columnMeta.map((c) => (
                              <TableCell key={c.index} className="text-xs tabular-nums">
                                {formatPreviewValue(r[c.index])}
                              </TableCell>
                            ))}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </div>
            )}
          </section>

          {/* Paso 3: Mapeo */}
          {headers.length > 0 && (
            <section className="border rounded-lg p-3 space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold">3. Mapeo de columnas</h3>
                <span className="text-xs text-muted-foreground">
                  {mappedCount} de {SCHEDULE_FIELDS.length} campos asignados
                </span>
              </div>
              {missingRequired.length > 0 && (
                <div className="text-xs text-destructive flex items-center gap-1">
                  <AlertCircle className="h-3.5 w-3.5" />
                  Faltan campos obligatorios: {missingRequired.join(", ")}
                </div>
              )}
              <p className="text-xs text-muted-foreground">
                Las columnas completamente vacías se ocultan. Si una columna no tiene título, se muestra con el primer valor detectado.
              </p>
              <div className="grid grid-cols-2 gap-2">
                {SCHEDULE_FIELDS.map((f) => (
                  <div key={f.key} className="grid grid-cols-[1fr_auto] items-center gap-2 rounded border p-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <Label className="text-xs truncate">
                        {f.label} {f.required && <span className="text-destructive">*</span>}
                      </Label>
                      {mapping[f.key] != null ? (
                        <CheckCircle2 className="h-3.5 w-3.5 text-green-500 shrink-0" />
                      ) : f.required ? (
                        <XCircle className="h-3.5 w-3.5 text-destructive shrink-0" />
                      ) : null}
                    </div>
                    <Select
                      value={mapping[f.key] != null ? String(mapping[f.key]) : "none"}
                      onValueChange={(v) => setMapping((m) => ({ ...m, [f.key]: v === "none" ? null : Number(v) }))}
                    >
                      <SelectTrigger className="h-8 text-xs w-56"><SelectValue /></SelectTrigger>
                      <SelectContent className="max-h-72">
                        <SelectItem value="none">— sin asignar —</SelectItem>
                        {columnMeta.map((c) => (
                          <SelectItem key={c.index} value={String(c.index)}>
                            {c.letter}: {c.header || "(sin título)"}
                            {c.firstValue ? ` — ${c.firstValue}` : ""}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Paso 4: Previsualización */}
          {preview.length > 0 && (
            <section className="border rounded-lg p-3 space-y-2">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold">4. Previsualización ({preview.length} cuotas)</h3>
                <div className="text-xs text-muted-foreground">
                  Σ Amort.: <b className="text-foreground">{formatNumber(totals.principal)}</b> ·
                  Σ Int.: <b className="text-foreground">{formatNumber(totals.interest)}</b> ·
                  Σ Total: <b className="text-foreground">{formatNumber(totals.total)}</b>
                </div>
              </div>
              <div className="max-h-60 overflow-y-auto border rounded">
                <Table>
                  <TableHeader className="sticky top-0 bg-muted">
                    <TableRow>
                      <TableHead className="text-xs">Nº</TableHead>
                      <TableHead className="text-xs">Fecha</TableHead>
                      <TableHead className="text-xs text-right">Amortiz.</TableHead>
                      <TableHead className="text-xs text-right">Interés</TableHead>
                      <TableHead className="text-xs text-right">Seguro</TableHead>
                      <TableHead className="text-xs text-right">Total</TableHead>
                      <TableHead className="text-xs text-right">Cap. pdte.</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {preview.slice(0, 100).map((r, i) => (
                      <TableRow key={i} className={!r.due_date ? "bg-destructive/10" : ""}>
                        <TableCell className="text-xs">{r.installment_number}</TableCell>
                        <TableCell className="text-xs">
                          {r.due_date || <span className="text-destructive inline-flex items-center gap-1"><AlertCircle className="h-3 w-3" />sin fecha</span>}
                        </TableCell>
                        <TableCell className="text-xs text-right tabular-nums">{formatNumber(r.principal)}</TableCell>
                        <TableCell className="text-xs text-right tabular-nums">{formatNumber(r.interest)}</TableCell>
                        <TableCell className="text-xs text-right tabular-nums">{formatNumber(r.insurance)}</TableCell>
                        <TableCell className="text-xs text-right tabular-nums">{formatNumber(r.total)}</TableCell>
                        <TableCell className="text-xs text-right tabular-nums">{r.pending_capital != null ? formatNumber(r.pending_capital) : "—"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                {preview.length > 100 && (
                  <div className="text-[11px] text-muted-foreground p-2 text-center">
                    Mostrando 100 de {preview.length} filas. Se importarán todas.
                  </div>
                )}
              </div>
            </section>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={doImport} disabled={!canImport || importing}>
            <CheckCircle2 className="h-4 w-4 mr-2" />
            {importing ? "Importando…" : `Importar ${preview.length} cuotas`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ImportScheduleDialog;
